import ClassNames from "classnames";
import { FunctionComponent, useEffect, useRef } from "react";
import { getSiteModule } from "../../selectors/modules.js";
import { selectActiveSite } from "../../selectors/sites.js";
import { useAppSelector } from "../../store/hooks.js";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import styles from "../../stylesheets/components/quote-module.module.css";
import {
  AspectRatio,
  BaseModuleProps,
  QuoteModuleSettings,
  ThreeSizes,
} from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import {
  addAndLoadFonts,
  checkFontExists,
  deleteFonts,
  getFontFaces,
} from "../../utils/fonts.js";
import {
  getActiveColorScheme,
  getImageSubModule,
  round,
} from "../../utils/utils.js";
import ModuleHeadings from "../ModuleHeadings.js";
import ModuleWithHeadings from "../ModuleWithHeadings.js";
import MultilineText from "../MultilineText.js";
import ImageModule from "./ImageModule.js";
import QuoteIcon from "./QuoteIcon.js";

type Props = BaseModuleProps<QuoteModuleSettings>;

const QuoteModule: FunctionComponent<Props> = ({
  translatedModule: {
    id: moduleId,
    settings: { textAlign, layout, mediaAlign, fontSize },
    translation: {
      languageId,
      settings: { title, subtitle, quote, author },
    },
  },
  translatedModule,
  isFirstOnPage,
  isPreview,
  pageId,
  queries,
}) => {
  const site = useAppSelector(selectActiveSite);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, site, translatedModule),
  );
  const modules = useAppSelector((state) => state.modules);
  const image = getImageSubModule({ languageId, pageId, moduleId, modules });
  const font = useAppSelector((state) => {
    const { fonts, modules } = state;
    const fontFamily =
      getSiteModule<QuoteModuleSettings>({
        languageId,
        moduleType: "QuoteModule",
        modules,
      })?.settings.fontFamily ?? undefined;
    const defaultFontFamily = site.titleFontFamily ?? site.fontFamily;
    return fontFamily
      ? fonts.byFamily[fontFamily]
      : fonts.byFamily[defaultFontFamily];
  });

  const loadedFontsRef = useRef<FontFace[]>([]);

  useEffect(() => {
    // Abort if font is already loaded or if the
    // fonts API response is not yet loaded.
    if (!font || checkFontExists(font.family)) {
      return;
    }
    const fontFaces = getFontFaces(font);
    addAndLoadFonts(fontFaces);
    loadedFontsRef.current = fontFaces;
  }, [font]);

  useEffect(() => {
    return () => {
      deleteFonts(loadedFontsRef.current);
      loadedFontsRef.current = [];
    };
  }, [loadedFontsRef]);

  const fixedAspectRatio: AspectRatio = 1.7778;

  return (
    <ModuleWithHeadings
      className={ClassNames(
        styles.QuoteModule,
        styles[`QuoteModule--${layout}`],
      )}
      id={moduleId}
      colors={{ background: scheme.main.background, color: scheme.main.text }}
      title={title}
      subtitle={subtitle}
    >
      <div
        className={ClassNames(
          moduleBaseStyles.Module__Wrapper,
          moduleBaseStyles["Module__Wrapper--default-padding"],
        )}
      >
        <ModuleHeadings
          scheme={scheme}
          isFirstOnPage={isFirstOnPage}
          textAlign={textAlign}
          title={title}
          subtitle={subtitle}
        />
        <div
          className={ClassNames(styles.QuoteModule__WrapperInner, {
            [styles[`QuoteModule__WrapperInner--media-${mediaAlign}`]]: image,
            [styles["QuoteModule__WrapperInner--text-image"]]: image,
          })}
        >
          {image && (
            <div className={styles.QuoteModule__Image}>
              <ImageModule
                aspectRatio={
                  queries["Query--medium"] ? undefined : fixedAspectRatio
                }
                lazyLoad={true}
                translatedModule={image}
                width={600}
                sizes="(min-width: 640px) 40vw, (min-width: 1600px) 600px, 100vw"
                isPreview={isPreview}
                isSlider={false}
                pageId={pageId}
                scheme={scheme}
                showOverlay={false}
              />
            </div>
          )}
          {(author || quote) && (
            <div
              className={ClassNames(
                styles.QuoteModule__Text,
                `TextAlign--${textAlign.description}`,
              )}
            >
              {quote && (
                <div className={styles.QuoteModule__Quote}>
                  <QuoteIcon
                    layout={layout}
                    scheme={scheme}
                    mediaAlign={mediaAlign}
                    type="open"
                  />
                  <span
                    className={ClassNames(
                      css({
                        fontFamily: font?.family,
                        fontSize:
                          getFontSize(font?.scale ?? 1, fontSize) + "em",
                      }),
                    )}
                  >
                    <MultilineText text={quote} />
                  </span>
                  <QuoteIcon
                    layout={layout}
                    scheme={scheme}
                    mediaAlign={mediaAlign}
                    type="close"
                  />
                </div>
              )}
              {author && (
                <div className={styles.QuoteModule__Author}>{author}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </ModuleWithHeadings>
  );
};

const getFontSize = (fontScale: number, sizeSetting: ThreeSizes): number => {
  const sizesMap: { [key in ThreeSizes]: number } = {
    small: 0.8,
    medium: 1,
    big: 1.15,
  };

  return round(fontScale * sizesMap[sizeSetting], 2);
};

export default QuoteModule;
